import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext.jsx';

/**
 * Pi Network Login Button Component
 * Handles Pi Network authentication with loading states and error handling
 */
const PiLoginButton = ({ 
  className = '', 
  size = 'lg',
  disabled = false,
  onSuccess,
  onError 
}) => {
  const { loginWithPi, loading: authLoading, isPiBrowser, isPiBrowserDetecting } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Handle Pi Network login
   */
  const handlePiLogin = async () => {
    if (disabled || isLoading || authLoading) return;

    try {
      setIsLoading(true);
      
      const result = await loginWithPi();
      
      if (result.success) {
        // Call success callback if provided
        if (onSuccess) {
          onSuccess(result);
        }
      } else {
        // Call error callback if provided
        if (onError) {
          onError(result.error);
        }
      }
    } catch (error) {
      console.error('Pi login button error:', error);
      
      // Call error callback if provided
      if (onError) {
        onError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Size variants
  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-3 text-base',
    xl: 'px-10 py-5 text-xl'
  };

  // Loading state - include Pi Browser detection
  const isButtonLoading = isLoading || authLoading || isPiBrowserDetecting;
  
  // Determine if button should be disabled
  const isButtonDisabled = disabled || isButtonLoading || (isPiBrowser === false);

  return (
    <button
      onClick={handlePiLogin}
      disabled={isButtonDisabled}
      title={isPiBrowser === false ? 'Pi Network login is only available in Pi Browser' : ''}
      className={`
        relative
        inline-flex
        items-center
        justify-center
        font-semibold
        rounded-full
        transition-all
        duration-200
        transform
        focus:outline-none
        focus:ring-4
        focus:ring-primary-teal/20
        ${sizeClasses[size]}
        ${
          isButtonDisabled
            ? 'bg-neutral-300 text-neutral-500 cursor-not-allowed'
            : 'bg-primary-teal hover:bg-primary-teal/90 text-white hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl'
        }
        ${className}
      `}
      type="button"
    >
      {/* Loading spinner */}
      {isButtonLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        </div>
      )}
      
      {/* Button content */}
      <div className={`flex items-center gap-3 ${isButtonLoading ? 'opacity-0' : 'opacity-100'}`}>
        {/* Pi Network Icon */}
        <div className="flex items-center justify-center w-6 h-6 bg-white/20 rounded-full">
          <img
            src="/images/pi.png"
            alt="Pi Network"
            className="w-4 h-4"
          />
        </div>
        
        <span>
          {isPiBrowserDetecting
            ? 'Detecting Pi Browser...'
            : isPiBrowser === false
            ? 'Pi Browser Required'
            : 'Continue with Pi Network'}
        </span>
      </div>
    </button>
  );
};

export default PiLoginButton;
